import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Trophy, CalendarDays, Network, MessageSquare, Bookmark, Search as SearchIcon, User, ShieldCheck, Palette, CreditCard, UserPlus, UserCheck, BookOpen, Bell, ClipboardList, Ruler } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Guard, homeFor } from "./components/app/Guard";
import { AppShell } from "./components/app/AppShell";
import { useApi } from "./hooks/useApi";
import { web } from "./lib/api";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Join from "./pages/Join";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import Billing from "./pages/coach/Billing";
import { PaymentSuccess, PaymentCancel } from "./pages/Payment";
import Overview from "./pages/coach/Overview";
import Roster from "./pages/coach/Roster";
import PlayerDetail from "./pages/coach/PlayerDetail";
import { Games, GameDetail } from "./pages/coach/Games";
import { Schedule } from "./pages/coach/Schedule";
import DepthChart from "./pages/coach/DepthChart";
import CoachMessages from "./pages/coach/Messages";
import StatEntry from "./pages/coach/StatEntry";
import EnterStats from "./pages/coach/EnterStats";
import Reel from "./pages/Reel";
import AcceptInvite from "./pages/AcceptInvite";
import TeamAccess from "./pages/coach/TeamAccess";
import AttendancePage from "./pages/coach/AttendancePage";
import Playbook from "./pages/coach/Playbook";
import { LevelSwitch } from "./components/app/LevelSwitch";
import Board from "./pages/recruiter/Board";
import Search from "./pages/recruiter/Search";
import Prospect from "./pages/recruiter/Prospect";
import RecruiterMessages from "./pages/recruiter/Messages";
import Branding from "./pages/recruiter/Branding";
import Alerts, { AlertDetail } from "./pages/recruiter/Alerts";
import RecruiterSchedule from "./pages/recruiter/RecruiterSchedule";
import FreeAccess from "./pages/admin/FreeAccess";
import Benchmarks from "./pages/admin/Benchmarks";
import RecruiterPlan, { useRecruiterPlan } from "./pages/recruiter/Plan";
import { Loader } from "./components/app/kit";
import { useFamilyRole } from "./hooks/useFamilyRole";
import DemoLayout from "./pages/demo/DemoLayout";
import DemoTeam from "./pages/demo/DemoTeam";
import DemoPlayer from "./pages/demo/DemoPlayer";
import DemoRecruiter from "./pages/demo/DemoRecruiter";
import Visibility from "./pages/Visibility";
import FanTeam from "./pages/FanTeam";
import RedeemInvite from "./pages/RedeemInvite";

const ADMIN_ITEM = { to: "/admin", label: "Free access", icon: ShieldCheck, end: true };
const BENCH_ITEM = { to: "/admin/benchmarks", label: "Benchmarks", icon: Ruler };

const CoachArea = () => {
  const { membership, isAdmin, canEdit, canRun } = useAuth();
  const tid = membership?.team_id;
  const isPlayer = membership?.role === "player";
  const { isParent } = useFamilyRole();
  const items = [
    { to: "/coach", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/coach/roster", label: "Roster", icon: Users },
    isPlayer && membership?.player_id && { to: `/coach/players/${membership.player_id}`, label: isParent ? "My player" : "My profile", icon: User },
    { to: "/coach/games", label: "Games", icon: Trophy },
    canEdit && { to: "/coach/enter-stats", label: "Enter stats", icon: ClipboardList },
    { to: "/coach/schedule", label: "Schedule", icon: CalendarDays },
    canRun && { to: "/coach/attendance", label: "Attendance", icon: UserCheck },
    { to: "/coach/playbook", label: "Playbook", icon: BookOpen },
    { to: "/coach/depth", label: "Depth chart", icon: Network },
    !isPlayer && { to: "/coach/messages", label: "Messages", icon: MessageSquare, unread: true },
    canEdit && { to: "/coach/team", label: "Team access", icon: UserPlus },
    membership?.role === "owner" && { to: "/coach/billing", label: "Subscription", icon: CreditCard },
    isAdmin && ADMIN_ITEM,
    isAdmin && BENCH_ITEM,
  ].filter(Boolean);
  return <AppShell items={items} home="/coach" extra={<LevelSwitch />} unreadPath={tid && !isPlayer ? `/teams/${tid}/messages/unread_count` : null} />;
};

const useAlertCount = (enabled) => {
  const loc = useLocation();
  const r = useApi(enabled && "/recruiter/alerts/unread_count", web);
  const { reload } = r;
  useEffect(() => { if (enabled) reload(); }, [loc.pathname, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener("gd-alerts", reload);
    return () => window.removeEventListener("gd-alerts", reload);
  }, [reload]);
  return r.data?.count ?? 0;
};

const RecruiterArea = () => {
  const { isAdmin } = useAuth();
  const sub = useRecruiterPlan();
  const alertCount = useAlertCount(!!sub.data?.active);
  if (sub.loading && !sub.data) return <div className="min-h-screen bg-[#0A0E1A] pt-32"><Loader /></div>;
  const locked = !sub.data?.active;
  const items = (locked ? [
    { to: "/recruiter", label: "Start free trial", icon: CreditCard, end: true },
  ] : [
    { to: "/recruiter", label: "Board", icon: Bookmark, end: true },
    { to: "/recruiter/search", label: "Search", icon: SearchIcon },
    { to: "/recruiter/schedule", label: "Schedule", icon: CalendarDays },
    { to: "/recruiter/alerts", label: "Alerts", icon: Bell, badge: alertCount },
    { to: "/recruiter/messages", label: "Messages", icon: MessageSquare, unread: true },
    { to: "/recruiter/branding", label: "Card branding", icon: Palette },
    { to: "/recruiter/subscription", label: "Subscription", icon: CreditCard },
  ]).concat(isAdmin ? [ADMIN_ITEM, BENCH_ITEM] : []);
  return (
    <AppShell items={items} home="/recruiter" unreadPath={locked ? null : "/recruiter/messages/unread_count"}>
      {locked ? <RecruiterPlan sub={sub} /> : null}
    </AppShell>
  );
};

const AdminArea = () => {
  const { me } = useAuth();
  const items = [ADMIN_ITEM, BENCH_ITEM, { to: homeFor(me), label: "Dashboard", icon: LayoutDashboard, end: true }];
  return <AppShell items={items} home={homeFor(me)} unreadPath={null} />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/p/:token" element={<Reel />} />
          <Route path="/invite/:inviteId" element={<AcceptInvite />} />
          <Route path="/visibility" element={<Visibility />} />
          <Route path="/team/:slug" element={<FanTeam />} />
          <Route path="/invite-code" element={<RedeemInvite />} />
          <Route path="/demo" element={<DemoLayout />}>
            <Route index element={<DemoTeam />} />
            <Route path="players/:playerId" element={<DemoPlayer />} />
            <Route path="recruiter" element={<DemoRecruiter />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/join" element={<Join />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/coach" element={<Guard role="team"><CoachArea /></Guard>}>
            <Route index element={<Overview />} />
            <Route path="roster" element={<Roster />} />
            <Route path="players/:playerId" element={<PlayerDetail />} />
            <Route path="games" element={<Games />} />
            <Route path="games/:gameId" element={<GameDetail />} />
            <Route path="games/:gameId/stats" element={<StatEntry />} />
            <Route path="enter-stats" element={<EnterStats />} />
            <Route path="team" element={<TeamAccess />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="playbook" element={<Playbook />} />
            <Route path="depth" element={<DepthChart />} />
            <Route path="messages" element={<CoachMessages />} />
            <Route path="billing" element={<Billing />} />
          </Route>
          <Route path="/recruiter" element={<Guard role="recruiter"><RecruiterArea /></Guard>}>
            <Route index element={<Board />} />
            <Route path="search" element={<Search />} />
            <Route path="prospects/:playerId" element={<Prospect />} />
            <Route path="messages" element={<RecruiterMessages />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="schedule" element={<RecruiterSchedule />} />
            <Route path="alerts/:alertId" element={<AlertDetail />} />
            <Route path="branding" element={<Branding />} />
            <Route path="subscription" element={<RecruiterPlan />} />
          </Route>
          <Route path="/admin" element={<Guard role="admin"><AdminArea /></Guard>}>
            <Route index element={<FreeAccess />} />
            <Route path="benchmarks" element={<Benchmarks />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </AuthProvider>
  );
}

export default App;
